import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { colors2 } from 'oolib';
import { FloatingPortal } from '../hooks/useFloatingPosition';
import {
    BORDER_RADIUS,
    renderContent,
    resolvePrimaryContent,
    CURSOR_HEIGHT,
    VIEWPORT_PADDING,
} from '../utils/index';
import {
    StyledCursorTooltipWrapper,
    StyledTooltipContent,
} from '../styled';
import { TooltipPropsControlled } from '../types';


// CONTROLLED MODE COMPONENT (For Recharts)
export default function ControlledTooltip({
    text,
    heading,
    primaryContent,
    secondaryContent,
    progressiveDelay = 600,
    progressiveGap = 8,
    invert = false,
    cursorOffset = 0,
    style,
    isOpen = false,
    mousePosition,
}: TooltipPropsControlled) {
    const bgColor = invert ? colors2.white : colors2.black;
    const textColor = invert ? colors2.black : colors2.white;
    const resolvedPrimaryContent = resolvePrimaryContent(text, heading, primaryContent);

    const [showSecondary, setShowSecondary] = useState(false);
    const [cursorTooltipStyle, setCursorTooltipStyle] = useState<React.CSSProperties>({
        position: 'fixed',
        visibility: 'hidden',
        top: 0,
        left: 0,
    });
    const tooltipRef = useRef<HTMLDivElement>(null);
    const progressiveTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Progressive tooltip logic
    useEffect(() => {
        if (isOpen && secondaryContent) {
            progressiveTimerRef.current = setTimeout(() => setShowSecondary(true), progressiveDelay);
        } else {
            setShowSecondary(false);
            if (progressiveTimerRef.current) clearTimeout(progressiveTimerRef.current);
        }
        return () => {
            if (progressiveTimerRef.current) clearTimeout(progressiveTimerRef.current);
        };
    }, [isOpen, progressiveDelay, secondaryContent]);

    // Calculate cursor tooltip position - useLayoutEffect to prevent flash
    useLayoutEffect(() => {
        if (!mousePosition || !tooltipRef.current) {
            return;
        }

        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        let top = mousePosition.y + CURSOR_HEIGHT + cursorOffset;
        let left = mousePosition.x + 10;

        if (top + tooltipRect.height > viewportHeight - VIEWPORT_PADDING) {
            top = mousePosition.y - tooltipRect.height - cursorOffset;
        }
        if (left + tooltipRect.width > viewportWidth - VIEWPORT_PADDING) {
            left = viewportWidth - tooltipRect.width - VIEWPORT_PADDING;
        }
        if (left < VIEWPORT_PADDING) {
            left = VIEWPORT_PADDING;
        }

        setCursorTooltipStyle({
            position: 'fixed',
            visibility: 'visible',
            top: `${top}px`,
            left: `${left}px`,
        });
    }, [mousePosition, cursorOffset]);

    // Don't render if not open or no content
    if (!isOpen || !resolvedPrimaryContent) {
        return null;
    }

    const cursorBorderRadius = secondaryContent && showSecondary && progressiveGap === 0
        ? `${BORDER_RADIUS} ${BORDER_RADIUS} 0 0`
        : BORDER_RADIUS;
    const cursorSecondaryBorderRadius = progressiveGap === 0
        ? `0 0 ${BORDER_RADIUS} ${BORDER_RADIUS}`
        : BORDER_RADIUS;

    return (
        <FloatingPortal>
            <StyledCursorTooltipWrapper
                ref={tooltipRef}
                style={{ ...cursorTooltipStyle, ...style }}
                $gap={progressiveGap}
            >
                <StyledTooltipContent
                    $bgColor={bgColor}
                    $textColor={textColor}
                    $borderRadius={cursorBorderRadius}
                >
                    {renderContent(resolvedPrimaryContent)}
                </StyledTooltipContent>
                {secondaryContent && showSecondary && (
                    <StyledTooltipContent
                        $bgColor={bgColor}
                        $textColor={textColor}
                        $borderRadius={cursorSecondaryBorderRadius}
                    >
                        {renderContent(secondaryContent)}
                    </StyledTooltipContent>
                )}
            </StyledCursorTooltipWrapper>
        </FloatingPortal>
    );
}